import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

const primaryButtonStyle = {
    padding: '10px 18px', borderRadius: '6px', border: 'none',
    background: '#2563eb', color: '#fff', fontWeight: 600, fontSize: '0.88em',
    cursor: 'pointer', font: 'inherit',
};

const secondaryButtonStyle = {
    padding: '10px 18px', borderRadius: '6px', border: '1px solid #d1d5db',
    background: '#fff', color: '#374151', fontWeight: 500, fontSize: '0.88em',
    cursor: 'pointer', font: 'inherit',
};

const linkButtonStyle = {
    border: 'none', background: 'none', color: '#2563eb', fontSize: '0.85em',
    fontWeight: 600, cursor: 'pointer', font: 'inherit', padding: 0,
};

const smallSecondaryButtonStyle = {
    padding: '4px 10px', borderRadius: '5px', border: '1px solid #d1d5db',
    background: '#fff', color: '#374151', fontWeight: 500, fontSize: '0.78em',
    cursor: 'pointer', font: 'inherit', flexShrink: 0,
};

const breadcrumbItemStyle = (isLast) => ({
    border: 'none', background: 'none', padding: 0, font: 'inherit',
    fontSize: '0.85em', cursor: isLast ? 'default' : 'pointer',
    color: isLast ? '#111827' : '#2563eb',
    fontWeight: isLast ? 600 : 500,
    textDecoration: isLast ? 'none' : 'underline',
});

// Extrait un message d'erreur exploitable d'une réponse HTTP en échec (même
// logique que ClientBuildingDashboard.js : le backend renvoie parfois du
// JSON, parfois une page d'erreur brute non parsable).
const parseErrorResponse = async (response, fallbackMessage) => {
    try {
        const data = await response.json();
        if (data && data.error) return data.error;
    } catch (e) {
        // réponse non-JSON, on garde le fallback
    }
    return `${fallbackMessage} (HTTP ${response.status})`;
};

const ROOT_LABEL = 'Dossier des rapports';

/**
 * Modale déclenchée par le bouton "Générer le rapport" du dashboard bâtiment.
 * Laisse naviguer dans l'arborescence du Shared Drive (fil d'Ariane, entrée
 * dans les sous-dossiers) pour choisir où ranger le Google Sheet, avec
 * possibilité de créer un nouveau sous-dossier à l'endroit courant.
 */
export default function ReportFolderPickerModal({ buildingId, onClose }) {
    // path[0].id vaut toujours null (racine) ; son vrai id Drive est résolu
    // à part dans rootFolderId dès la première réponse de l'API, pour rester
    // sélectionnable sans provoquer un second fetch (voir effet ci-dessous).
    const [path, setPath] = useState([{ id: null, name: ROOT_LABEL }]);
    const [rootFolderId, setRootFolderId] = useState(null);
    const [folders, setFolders] = useState([]);
    const [loadingFolders, setLoadingFolders] = useState(true);
    const [selectedFolder, setSelectedFolder] = useState(null); // { id, label }
    const [query, setQuery] = useState('');
    const [creating, setCreating] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [creatingFolder, setCreatingFolder] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState(null);
    const [currentReport, setCurrentReport] = useState(null); // { sheet_url, sheet_name, folder_path }
    const containerRef = useRef(null);
    const busyRef = useRef(false);
    busyRef.current = isGenerating || creatingFolder;

    const currentParentId = path[path.length - 1].id;
    const currentFolderId = currentParentId || rootFolderId;

    useEffect(() => {
        let cancelled = false;
        const loadFolders = async () => {
            setLoadingFolders(true);
            setError(null);
            try {
                const url = currentParentId
                    ? `${process.env.REACT_APP_API_URL}/report_folders?parent_id=${encodeURIComponent(currentParentId)}`
                    : `${process.env.REACT_APP_API_URL}/report_folders`;
                const response = await fetch(url);
                if (!response.ok) throw new Error(await parseErrorResponse(response, "Impossible de charger les dossiers"));
                const data = await response.json();
                if (cancelled) return;
                setFolders(data.folders || []);
                if (!currentParentId && data.parent_id) setRootFolderId(data.parent_id);
            } catch (e) {
                if (!cancelled) setError(e.message);
            } finally {
                if (!cancelled) setLoadingFolders(false);
            }
        };
        loadFolders();
        return () => { cancelled = true; };
    }, [currentParentId]);

    // Rapport déjà généré : on ouvre la modale sur son dossier, présélectionné,
    // pour qu'un nouvel export le mette à jour sans le déplacer par mégarde.
    useEffect(() => {
        let cancelled = false;
        const loadCurrentReport = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/report_location/${buildingId}`);
                if (!response.ok) return;
                const data = await response.json();
                if (cancelled || !data.report) return;
                setCurrentReport(data.report);
                const folderPath = data.report.folder_path;
                if (folderPath) {
                    if (folderPath.length > 0) setPath([{ id: null, name: ROOT_LABEL }, ...folderPath]);
                    setSelectedFolder({
                        id: data.report.folder_id,
                        label: [ROOT_LABEL, ...folderPath.map((p) => p.name)].join(' / '),
                    });
                }
            } catch (e) {
                // sans emplacement connu, la modale part simplement de la racine
            }
        };
        loadCurrentReport();
        return () => { cancelled = true; };
    }, [buildingId]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (busyRef.current) return; // pas de fermeture pendant une génération/création
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    const normalizedQuery = query.trim().toLowerCase();
    const filteredFolders = normalizedQuery
        ? folders.filter((f) => f.name.toLowerCase().includes(normalizedQuery))
        : folders;

    const pathLabel = (names) => names.join(' / ');

    const enterFolder = (folder) => {
        setPath((prev) => [...prev, { id: folder.id, name: folder.name }]);
        setQuery('');
        setCreating(false);
    };

    const jumpToBreadcrumb = (index) => {
        setPath((prev) => prev.slice(0, index + 1));
        setQuery('');
        setCreating(false);
    };

    const selectFolder = (folder) => {
        setSelectedFolder({
            id: folder.id,
            label: pathLabel([...path.slice(1).map((p) => p.name), folder.name]) || folder.name,
        });
    };

    const selectCurrentFolder = () => {
        if (!currentFolderId) return;
        setSelectedFolder({
            id: currentFolderId,
            label: pathLabel(path.map((p) => p.name)),
        });
    };

    const handleCreateFolder = async () => {
        const name = newFolderName.trim();
        if (!name) return;
        setCreatingFolder(true);
        setError(null);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/report_folders`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, parent_id: currentParentId || undefined }),
            });
            if (!response.ok) throw new Error(await parseErrorResponse(response, "Impossible de créer le dossier"));
            const folder = await response.json();
            const newEntry = { id: folder.folder_id, name: folder.name };
            setFolders((prev) => (prev.some((f) => f.id === newEntry.id) ? prev : [...prev, newEntry]));
            selectFolder(newEntry);
            setCreating(false);
            setNewFolderName('');
        } catch (e) {
            setError(e.message);
        } finally {
            setCreatingFolder(false);
        }
    };

    const handleConfirm = async () => {
        if (!selectedFolder) {
            setError("Choisissez un dossier de destination.");
            return;
        }
        setIsGenerating(true);
        setError(null);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/generate_report_sheet/${buildingId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ folder_id: selectedFolder.id }),
            });
            if (!response.ok) throw new Error(await parseErrorResponse(response, "Erreur lors de la génération du rapport"));
            const data = await response.json();
            toast.success(
                <span>
                    Rapport généré.{' '}
                    <a href={data.sheet_url} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                        Ouvrir le Google Sheet
                    </a>
                </span>
            );
            onClose();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div className="section-popup">
            <div className="section-popup-content" style={{ maxWidth: '520px' }} ref={containerRef}>
                <span className="close" onClick={onClose}>&times;</span>
                <h2>Générer le rapport Google Sheet</h2>

                {currentReport && (
                    <p style={{ fontSize: '0.85em', color: '#374151', marginTop: 0, marginBottom: '14px', padding: '8px 10px', background: '#f3f4f6', borderRadius: '6px' }}>
                        Rapport existant :{' '}
                        <a href={currentReport.sheet_url} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontWeight: 600 }}>
                            {currentReport.sheet_name}
                        </a>
                        {currentReport.folder_path
                            ? <> dans <strong>{[ROOT_LABEL, ...currentReport.folder_path.map((p) => p.name)].join(' / ')}</strong>. Il sera mis à jour, et déplacé si vous choisissez un autre dossier.</>
                            : <> (rangé hors du dossier des rapports). Il sera mis à jour et déplacé dans le dossier choisi.</>}
                    </p>
                )}

                <label style={{ display: 'block', fontSize: '0.85em', fontWeight: 600, marginBottom: '6px' }}>
                    Dossier de destination
                </label>

                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px', marginBottom: '8px', minHeight: '20px' }}>
                    {path.map((segment, index) => (
                        <React.Fragment key={segment.id ?? 'root'}>
                            {index > 0 && <span style={{ color: '#9ca3af', fontSize: '0.85em' }}>/</span>}
                            <button
                                type="button"
                                style={breadcrumbItemStyle(index === path.length - 1)}
                                onClick={() => jumpToBreadcrumb(index)}
                                disabled={index === path.length - 1}
                            >
                                {segment.name}
                            </button>
                        </React.Fragment>
                    ))}
                </div>

                {creating ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <input
                            type="text"
                            autoFocus
                            placeholder="Nom du nouveau dossier"
                            value={newFolderName}
                            onChange={(e) => setNewFolderName(e.target.value)}
                            style={{ width: '100%', boxSizing: 'border-box', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', font: 'inherit' }}
                        />
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button type="button" style={primaryButtonStyle} onClick={handleCreateFolder} disabled={creatingFolder || !newFolderName.trim()}>
                                {creatingFolder ? 'Création…' : 'Créer'}
                            </button>
                            <button type="button" style={secondaryButtonStyle} onClick={() => setCreating(false)} disabled={creatingFolder}>
                                Annuler
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <input
                            type="text"
                            placeholder="Rechercher un dossier ici…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            style={{ width: '100%', boxSizing: 'border-box', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', font: 'inherit', marginBottom: '8px' }}
                        />

                        {currentFolderId && (
                            <button
                                type="button"
                                style={{ ...smallSecondaryButtonStyle, marginBottom: '8px', width: '100%', textAlign: 'left' }}
                                onClick={selectCurrentFolder}
                            >
                                ✓ Utiliser ce dossier ({path[path.length - 1].name}) comme destination
                            </button>
                        )}

                        <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '4px' }}>
                            {loadingFolders && (
                                <div style={{ padding: '10px 12px', color: '#6b7280', fontSize: '0.88em' }}>Chargement…</div>
                            )}
                            {!loadingFolders && filteredFolders.length === 0 && (
                                <div style={{ padding: '10px 12px', color: '#6b7280', fontSize: '0.88em' }}>Aucun sous-dossier ici.</div>
                            )}
                            {!loadingFolders && filteredFolders.map((folder) => (
                                <div
                                    key={folder.id}
                                    style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
                                        padding: '6px 8px', borderRadius: '6px',
                                        background: selectedFolder?.id === folder.id ? '#eff6ff' : 'transparent',
                                    }}
                                >
                                    <button
                                        type="button"
                                        onClick={() => enterFolder(folder)}
                                        style={{
                                            flex: 1, textAlign: 'left', border: 'none', background: 'none',
                                            cursor: 'pointer', font: 'inherit', padding: '2px 0',
                                            color: selectedFolder?.id === folder.id ? '#1d4ed8' : '#374151',
                                        }}
                                        title="Ouvrir ce dossier"
                                    >
                                        📁 {folder.name} ›
                                    </button>
                                    <button
                                        type="button"
                                        style={smallSecondaryButtonStyle}
                                        onClick={() => selectFolder(folder)}
                                    >
                                        Choisir
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div style={{ marginTop: '8px' }}>
                            <button type="button" style={linkButtonStyle} onClick={() => { setCreating(true); setNewFolderName(query); }}>
                                + Créer un nouveau dossier ici
                            </button>
                        </div>
                    </>
                )}

                {selectedFolder && (
                    <p style={{ fontSize: '0.85em', color: '#374151', marginTop: '14px', marginBottom: 0 }}>
                        Destination sélectionnée : <strong>{selectedFolder.label}</strong>
                    </p>
                )}

                {error && (
                    <p style={{ color: '#b91c1c', fontSize: '0.85em', marginTop: '10px' }}>{error}</p>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '20px' }}>
                    <button
                        type="button"
                        style={primaryButtonStyle}
                        disabled={!selectedFolder || isGenerating}
                        onClick={handleConfirm}
                    >
                        {isGenerating && <span className="btn-spinner" />}
                        {isGenerating ? 'Génération…' : 'Générer le rapport'}
                    </button>
                    <button type="button" style={secondaryButtonStyle} onClick={onClose} disabled={isGenerating}>
                        Annuler
                    </button>
                </div>
            </div>
        </div>
    );
}
